import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocationStore } from "@/lib/store";

const ALL_TASK_IDS = [
  "geogrid",
  "category", "description", "services", "service_desc",
  "essentials", "reviews", "images",
  "posts", "publish_post", "schedule_post",
  "products", "publish_product",
  "social_sync", "missing_profiles",
];

const PREREQ_MAP: Record<string, string[]> = {
  "/gbp-optimization": ["geogrid"],
  "/profile-health": ["geogrid", "category", "description", "services", "service_desc"],
  "/reviews": ["geogrid", "category", "description", "services", "service_desc", "essentials"],
  "/media": ["geogrid", "category", "description", "services", "service_desc", "essentials", "reviews"],
  "/posts": ["geogrid", "category", "description", "services", "service_desc", "essentials", "reviews", "images"],
  "/products": ["geogrid", "category", "description", "services", "service_desc", "essentials", "reviews", "images", "posts", "publish_post", "schedule_post"],
  "/social": ["geogrid", "category", "description", "services", "service_desc", "essentials", "reviews", "images", "posts", "publish_post", "schedule_post", "products", "publish_product"],
  "/citations": ["geogrid", "category", "description", "services", "service_desc", "essentials", "reviews", "images", "posts", "publish_post", "schedule_post", "products", "publish_product"],
  "/listing": ALL_TASK_IDS.slice(),
  "/automation": ALL_TASK_IDS.slice(),
  "/reports": ALL_TASK_IDS.slice(),
  "/leaderboard": ALL_TASK_IDS.slice(),
};

const ALWAYS_UNLOCKED = [
  "/road-to-domination",
  "/dashboard",
  "/geogrid",
  "/clients",
  "/setup",
  "/settings",
  "/notifications",
  "/admin",
];

export function useRtdProgress(enablePolling = false) {
  const { selectedLocationId } = useLocationStore();

  const { data: locations } = useQuery<any[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id || "";

  const { data: progress } = useQuery<{
    completedTasks: string[];
    autoCompletedTasks?: string[];
    manualCompletedTasks?: string[];
    gbpAnalysis?: {
      photos: { count: number; thisMonth: number; lastAddedAt: string | null };
      reviews: { count: number; thisMonth: number; lastAddedAt: string | null };
      products: { count: number; thisMonth: number; lastAddedAt: string | null };
      postsEngine: { enabled: boolean; totalPosts: number };
      publishedPosts: { count: number; lastAt: string | null };
      scheduledPosts: { count: number; nextAt: string | null };
      socials: { connectedCount: number };
    };
  }>({
    queryKey: ["/api/locations", locationId, "rtd-progress"],
    queryFn: async () => {
      const res = await fetch(`/api/locations/${locationId}/rtd-progress`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch progress");
      return res.json();
    },
    enabled: !!locationId,
    refetchInterval: enablePolling ? 15000 : false,
    staleTime: enablePolling ? 0 : 5 * 60 * 1000,
  });

  const completedTasks = useMemo(() => {
    return new Set<string>(progress?.completedTasks ?? []);
  }, [progress]);

  const currentLevel = completedTasks.size;

  const canDoTask = useCallback((taskId: string) => {
    const idx = ALL_TASK_IDS.indexOf(taskId);
    if (idx === 0) return true;
    if (idx < 0) return false;
    return completedTasks.has(ALL_TASK_IDS[idx - 1]);
  }, [completedTasks]);

  const allComplete = ALL_TASK_IDS.every((t) => completedTasks.has(t));

  const isNavUnlocked = useCallback((url: string) => {
    if (ALWAYS_UNLOCKED.includes(url)) return true;
    if (allComplete) return true;
    const prereqs = PREREQ_MAP[url];
    if (!prereqs) return true;
    return prereqs.every((taskId) => completedTasks.has(taskId));
  }, [completedTasks, allComplete]);

  const gbpAnalysis = progress?.gbpAnalysis;

  return {
    completedTasks,
    currentLevel,
    canDoTask,
    isNavUnlocked,
    locationId,
    gbpAnalysis,
  };
}
